import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/site/SEO";
import { ArrowLeft, Clock, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/posts/${slug}`).then((res) => {
      setPost(res.data);
      setLoading(false);
    }).catch(() => {
      setNotFound(true);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-32 md:pt-40 pb-24">
        <div className="container-secqre">
          <div className="h-12 bg-[#F8F9FA] rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-[#F8F9FA] rounded w-1/2 mt-4 animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="pt-40 pb-24 text-center">
        <p className="text-[#475569]">Post not found.</p>
        <Link to="/blog" className="btn-primary mt-6">Back to blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-post">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover,
          author: { "@type": "Person", name: post.author },
          datePublished: post.date,
        }}
      />

      <article className="pt-32 md:pt-40 pb-24">
        <div className="container-secqre max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0A0A0A]" data-testid="blog-post-back">
            <ArrowLeft className="w-4 h-4" /> All stories
          </Link>

          <div className="mt-8 flex items-center gap-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-[#FFD600] text-[#0A0A0A] font-bold">{post.category}</span>
            <span className="inline-flex items-center gap-1.5 text-[#475569]"><Clock className="w-3.5 h-3.5" /> {post.read_time}</span>
            <span className="inline-flex items-center gap-1.5 text-[#475569]"><User className="w-3.5 h-3.5" /> {post.author}</span>
          </div>

          <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="blog-post-title">
            {post.title}
          </h1>

          <p className="mt-6 text-xl text-[#475569] leading-relaxed">{post.excerpt}</p>

          <div className="mt-10 rounded-2xl overflow-hidden border border-[#E2E8F0]">
            <img src={post.cover} alt={post.title} className="w-full" />
          </div>

          <div className="prose prose-lg mt-12 max-w-none text-[#0A0A0A]">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i} className="text-lg leading-relaxed text-[#0A0A0A] mt-6">{para}</p>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-[#E2E8F0]">
            <p className="text-sm text-[#475569]">Written by <span className="font-semibold text-[#0A0A0A]">{post.author}</span> · {post.date}</p>
          </div>
        </div>
      </article>
    </div>
  );
}
